import {
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationFarmResponse,
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationResponse,
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationSummaryResponse,
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationWeekResponse
} from '../api/HealthSystemApi';
import { getISOWeek, parseISO, subDays } from 'date-fns';

/**
 * Admin farms overview behind `/admin/farms-overview`.
 *
 * The screen answers one question: "is every farm registering the piglets it should be?".
 * `GET /admin/dashboard/piglets` answers it for every farm, not only the ones that broke a rule: a farm
 * that is doing fine says so through its `status`, because a farm silently missing from the list is
 * indistinguishable from a farm that does not exist. Reading time stays constant on a large estate through
 * the status filter and server-side paging rather than through farms being dropped.
 *
 * The row/summary shapes are aliases of the generated client rather than copies: the response is the
 * contract, and a hand-written duplicate of it only creates somewhere for the two to drift apart. What
 * lives here is what the API cannot express - the status, rule and severity enums (the API types all three
 * as plain strings), and the filter state the page keeps.
 *
 * Conventions from the API: `percentOfExpected` is a whole percentage (0-100+), never a fraction, and the
 * trend keeps the cycles a farm registered nothing in as `registered: 0` rather than omitting them - the gap
 * is the shape the reader is looking for.
 *
 * A farm is judged per production cycle, not per week: `registered`, `expected` and the status cover the
 * last completed cycles - as many as it takes to span at least `lookbackWeeks` weeks - and the cycle in
 * progress is never scored. A farm without a production schedule counts as a 1-week cycle.
 */
export type FarmsOverview = Omit<
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationResponse,
  'data' | 'summary'
> & {
  data: FarmOverviewRow[];
  /**
   * The share of its registrations a farm may leave unweighed before its birth weight badge turns amber.
   * Intersected in for the same reason `FarmOverviewRow.status` is, and optional for the same reason -
   * see below. Read from the response rather than hard-coded: it is tunable backend configuration, and a
   * hard-coded copy contradicts the badge beside it the first time it is retuned.
   */
  unmeasuredBirthWeightPercent?: number | null;
  summary: FarmsOverviewSummary & {
    /** Farms, monitored or not, whose mobile devices still have animals queued. Ignores the filters. */
    farmsWithPendingAnimals?: number | null;
    /** Farms with no antibiotic treatment registered in the last `lookbackWeeks` weeks. Ignores the filters. */
    farmsWithoutAntibiotics: number;
  };
};

/**
 * One production cycle of a farm's trend, oldest first. `endDate` is exclusive - the first day after it.
 * `expected` is already prorated for a cycle a schedule change cut short.
 */
export interface FarmOverviewCycle {
  startDate: string;
  endDate: string;
  registered: number;
  expected: number;
  percentOfExpected: number;
  /** False for the cycle in progress, which is drawn but never scored. */
  isComplete: boolean;
  /** Every week of the cycle, oldest first - including empty weeks and, for the running cycle, weeks to come. */
  weeks: FarmOverviewWeek[];
}

/** The per-week fields of the generated client, less the per-week `expected` the API no longer sends. */
export type FarmOverviewWeek = Omit<
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationWeekResponse,
  'expected'
>;

/**
 * `status` is intersected in rather than coming from the generated client: it shipped with this change and
 * `src/api/HealthSystemApi.ts` is only regenerated deliberately, because doing so also pulls in unrelated
 * in-flight backend changes. Drop the intersection on the next regeneration. Optional for the same reason -
 * a client built against the older response has no such field.
 *
 * The production cycle fields are typed here for the same reason, and the weekly ones they replaced are
 * omitted so nothing can read a field the API stopped sending. Those are required: the screen is built on
 * them, and the frontend and the API ship together.
 */
export type FarmOverviewRow = Omit<
  HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationFarmResponse,
  'expectedPerWeek' | 'consecutiveWeeksWithoutRegistrations' | 'weeks'
> & {
  status?: string | null;
  /** False when the farm leaves more than `unmeasuredBirthWeightPercent` of its registrations unweighed. */
  measuresBirthWeights?: boolean | null;
  /** Registrations carrying a birth weight that came off a scale in the first four days of life. */
  birthWeightMeasuredCount?: number | null;
  /** Registrations with no such weight. With the count above, this is everything the farm registered. */
  birthWeightMissingCount?: number | null;
  /** The share the count above is of what the farm registered, already rounded to one decimal. */
  birthWeightMissingPercent?: number | null;
  /**
   * Animals still queued on the farm's mobile devices - the figure the farm dashboard's unsynced device data
   * card leads with. Queued registrations are not in `registered` yet, so a farm that looks behind may only be
   * behind on syncing.
   */
  pendingAnimalCount?: number | null;
  /** Devices whose latest sync report still carried a queue. */
  pendingDeviceCount?: number | null;
  /** Piglets the farm is expected to register in one production cycle. Zero for a `NotMonitored` farm. */
  expectedPerCycle: number;
  /** Length in weeks of the farm's production cycle; 1 without a schedule. */
  cycleWeeks: number;
  hasProductionSchedule: boolean;
  /** How many of the most recent completed cycles in a row the farm registered nothing in. */
  consecutiveCyclesWithoutRegistrations: number;
  /** Completed cycles `registered`, `expected` and the status were scored over. Zero before the first one ends. */
  scoredCycles: number;
  /** First day of the scored cycles; null when there is none. */
  scoredFrom: string | null;
  /** Exclusive end of the scored cycles - the first day after them; null when there is none. */
  scoredTo: string | null;
  /** Antibiotic treatments registered in the last `lookbackWeeks` weeks. */
  antibioticTreatmentCount: number;
  /** The sparkline: one entry per cycle, oldest first, the cycle in progress last. */
  cycles: FarmOverviewCycle[];
  weeks: FarmOverviewWeek[];
};
export type FarmsOverviewSummary = HealthSystemAPIControllersDashboardsResponsesAdminPigletRegistrationSummaryResponse;

/**
 * The ISO week numbers a date range covers, for labels like "wk 37–40". `to` is exclusive, as the API sends
 * it, so the last week is the one holding the day before it.
 */
export const isoWeekSpan = (from: string, to: string): { first: number; last: number } => ({
  first: getISOWeek(parseISO(from)),
  last: getISOWeek(subDays(parseISO(to), 1))
});

/**
 * Which rule put a farm in the queue. Values are the strings the API sends; it declares the field as a
 * plain string with the allowed values only in prose, so nothing validates these for us.
 */
export enum FarmIssueRule {
  /** Registered nothing at all in the most recent weeks. */
  NoRegistrations = 'NoRegistrations',
  /** Registered, but well under the expected volume. */
  UnderRegistered = 'UnderRegistered',
  /** Registered suspiciously over expected - usually a double import or a stale sow count. */
  OverRegistered = 'OverRegistered'
}

export enum FarmIssueSeverity {
  Critical = 'Critical',
  Warning = 'Warning'
}

/**
 * Where a farm stands. Every row carries one, including the healthy farms - which is the difference
 * between this screen and the exception queue it grew out of.
 */
export enum FarmStatus {
  /** Broke a rule, and it is urgent. */
  Critical = 'Critical',
  /** Broke a rule, but it is not urgent. */
  Warning = 'Warning',
  /** Broke no rule: registering what it should be. */
  OnTrack = 'OnTrack',
  /** No expected weekly volume configured, so nothing can be judged about it. */
  NotMonitored = 'NotMonitored'
}

/**
 * The API's rule/severity fields are untyped strings, so a value the frontend has never heard of is a
 * possibility rather than an impossibility. Parsing case-insensitively and returning null on an unknown
 * value lets a row still render with its numbers instead of throwing the whole page away.
 */
const parseEnum = <T extends string>(values: T[], raw?: string | null): T | null =>
  values.find((value) => value.toLowerCase() === raw?.toLowerCase()) ?? null;

export const parseFarmIssueRule = (raw?: string | null): FarmIssueRule | null =>
  parseEnum(Object.values(FarmIssueRule), raw);

export const parseFarmIssueSeverity = (raw?: string | null): FarmIssueSeverity | null =>
  parseEnum(Object.values(FarmIssueSeverity), raw);

export const parseFarmStatus = (raw?: string | null): FarmStatus | null => parseEnum(Object.values(FarmStatus), raw);

/**
 * A row's status, falling back to the severity for a response that predates the field. A flagged farm is
 * the only thing the older shape could contain, so a row with neither reads as on track.
 */
export const farmStatusOf = (farm: FarmOverviewRow): FarmStatus => {
  const status = parseFarmStatus(farm.status);
  if (status) return status;

  return parseFarmIssueSeverity(farm.severity) === FarmIssueSeverity.Critical
    ? FarmStatus.Critical
    : FarmStatus.OnTrack;
};

/** True for a farm that broke a rule - the rows that carry an issue headline rather than a plain summary. */
export const isFlagged = (farm: FarmOverviewRow): boolean => {
  const status = farmStatusOf(farm);
  return status === FarmStatus.Critical || status === FarmStatus.Warning;
};

/**
 * Whether a farm weighs the piglets it registers - the second badge on a row, and its own filter.
 *
 * Three states rather than the backend's one boolean, because "weighed none of them" is a different
 * conversation from "weighed most of them": a farm over the tolerance forgets to weigh, a farm at zero
 * owns no scale or never plugged it in, and only the second is worth a phone call about hardware.
 *
 * Values are the wire strings for the `Weighing` filter parameter.
 */
export enum FarmWeighing {
  /** Within the tolerance the API reports as `unmeasuredBirthWeightPercent`. */
  OnTrack = 'OnTrack',
  /** Weighs some piglets, but leaves more than the tolerance unweighed. */
  Behind = 'Behind',
  /** Weighed nothing at all in the period - no scale in use. */
  NoScale = 'NoScale'
}

/**
 * Zero measured wins over the tolerance flag: a farm that weighed nothing is reported as not measuring
 * anyway, and on the off chance the tolerance is ever configured at 100% it would otherwise read as fine.
 */
const weighingOf = (measured: number, measures: boolean): FarmWeighing => {
  if (measured === 0) return FarmWeighing.NoScale;
  return measures ? FarmWeighing.OnTrack : FarmWeighing.Behind;
};

/**
 * How much of what a farm registered was really weighed at birth.
 *
 * `measured` counts weights that came off the farm's scale, and nothing else. A registration without one
 * still stores a birth weight - the backend estimates it from the piglet's weight category - so "has a
 * weight" would be true of very nearly every piglet in the database and would paint every farm green,
 * including a farm that owns no scale.
 */
export interface BirthWeightRecording {
  /** Registrations over the evaluated period - `measured` plus `missing`. */
  registered: number;
  measured: number;
  missing: number;
  /** `missing` as a share of `registered`, rounded to one decimal by the API. */
  missingPercent: number;
  /** `measured` as a share of `registered` - what the badge states, and the complement of the above. */
  measuredPercent: number;
  /** False when the farm is over the tolerance the API reports as `unmeasuredBirthWeightPercent`. */
  measures: boolean;
  /** The three states the badge distinguishes; see `FarmWeighing`. */
  weighing: FarmWeighing;
}

/**
 * The row's birth weight figures, or null when there is nothing to say about them: a response from before
 * the fields existed, or a farm that registered no piglets in the period. A farm with nothing to weigh has
 * not failed to weigh anything, and a green badge on that row would be the screen's own invention.
 */
export const birthWeightRecordingOf = (farm: FarmOverviewRow): BirthWeightRecording | null => {
  if (farm.measuresBirthWeights == null) return null;

  const measured = farm.birthWeightMeasuredCount ?? 0;
  const missing = farm.birthWeightMissingCount ?? 0;
  const registered = measured + missing;

  if (registered === 0) return null;

  const missingPercent = farm.birthWeightMissingPercent ?? 0;

  return {
    registered,
    measured,
    missing,
    missingPercent,
    // Derived from the share the API already rounded rather than recomputed from the counts, so the badge
    // and the flag beside it can never disagree about where the tolerance boundary falls.
    measuredPercent: 100 - missingPercent,
    measures: farm.measuresBirthWeights,
    weighing: weighingOf(measured, farm.measuresBirthWeights)
  };
};

/**
 * Order of the queue, as the API's `Sort` values. A field name, optionally suffixed `-desc`.
 *
 * Worst first - critical above warning, biggest shortfall first - is not a member: it is the API's own
 * default and is expressed by sending no `Sort` at all, so the page models it as `sort: null`. That keeps
 * one meaning for "nothing selected" across the whole query. It is not what the page opens on, though - see
 * `emptyFarmsOverviewQuery`.
 *
 * Member names say what the reader gets; the values are the wire strings and must match the API exactly.
 */
export enum FarmsOverviewSort {
  MostRegistered = 'registered-desc',
  FewestRegistered = 'registered',
  /** Most completed production cycles in a row without a registration first. */
  LongestSilent = 'cyclessilent-desc',
  ShortestSilent = 'cyclessilent',
  FarmName = 'farmname',
  FarmNumber = 'farmnumber',
  /** Most of its registrations weighed first; the farms that do use their scale. */
  MostWeighed = 'weighed-desc',
  /** Least weighed first - the farms to chase, with the ones at zero at the very top. */
  LeastWeighed = 'weighed'
}

/**
 * What the page asks the API for. Search, rule, severity, order and paging are all server-side, so this is
 * the whole query - there is no client-side narrowing or re-ordering of the returned page, which would only
 * ever rearrange the rows that happened to land on it.
 *
 * `page` is 0-based to match MUI's pagination; the API is 1-based and the service adds the offset.
 */
export interface FarmsOverviewQuery {
  searchText: string;
  rule: FarmIssueRule | null;
  severity: FarmIssueSeverity | null;
  /** null lists every farm, healthy ones included. */
  status: FarmStatus | null;
  /** Narrows on whether the farm weighs what it registers, independently of `status`. */
  weighing: FarmWeighing | null;
  /** Only the farms whose mobile devices still have animals queued. Combines with every other filter. */
  pendingOnly: boolean;
  /** Only the farms with no antibiotic treatment registered over the lookback. Combines with every other filter. */
  noAntibioticsOnly: boolean;
  /** null sends no `Sort` at all, which is the API's own default: worst first. */
  sort: FarmsOverviewSort | null;
  page: number;
  size: number;
}

export const DEFAULT_FARMS_OVERVIEW_PAGE_SIZE = 25;

export const emptyFarmsOverviewQuery: FarmsOverviewQuery = {
  searchText: '',
  rule: null,
  severity: null,
  // Opens on every farm rather than on the farms that need a call: the point of showing the healthy ones is
  // that they are visible without being asked for. "Needs attention" is one click away.
  status: null,
  weighing: null,
  pendingOnly: false,
  noAntibioticsOnly: false,
  // The page opens on most registered rather than on the API's worst-first default. Worst first is still one
  // click away, and stays what the API returns when no order is asked for.
  sort: FarmsOverviewSort.MostRegistered,
  page: 0,
  size: DEFAULT_FARMS_OVERVIEW_PAGE_SIZE
};

/**
 * Whether the reader has narrowed the queue - which is what tells an empty result "nothing matches your
 * filter" from "every farm is fine". Order is deliberately not part of it: re-ordering an empty list cannot
 * be why it is empty, and counting it would turn the good news into a filter complaint.
 */
export const hasActiveFilters = (query: FarmsOverviewQuery): boolean =>
  query.searchText.trim() !== '' ||
  query.rule !== null ||
  query.severity !== null ||
  query.status !== null ||
  query.weighing !== null ||
  query.pendingOnly ||
  query.noAntibioticsOnly;

/** A row's identity for React. `farmId` alone is enough: the API returns one row per farm. */
export const farmRowKey = (farm: FarmOverviewRow): string => farm.farmId;
